import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { formatScreensaverDelayLabel, SCREEN_SCREENSAVER_DELAY_OPTIONS } from "@/lib/screensaver-delay";
import { RGB565_FRAME_BYTES, SCREEN_FRAME_HEIGHT, SCREEN_FRAME_WIDTH } from "@/lib/screen-frame";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";
import { AlertCircle, CheckCircle2, Settings } from "lucide-react";
import { errAsync, ResultAsync } from "neverthrow";
import { type ChangeEvent, type PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState } from "react";

type ConfigureScreenAppProps = {
  publicDeviceId: string;
};

type BuildConfigurationLinkArgs = {
  publicDeviceId: string;
};

type FetchStatusArgs = {
  publicDeviceId: string;
};

type SpotifyProfile = {
  displayName?: string;
  avatarUrl?: string;
};

type ConfigureStatusResponse = {
  linked: boolean;
  profile?: SpotifyProfile;
};

type AuthoriseSpotifyArgs = {
  publicDeviceId: string;
};

type AuthoriseSpotifyResponse = {
  authoriseUrl: string;
};

type ScreenSettings = {
  brightness: number;
  screenRotation: number;
  screensaverEnabled: boolean;
  showScreensaverWithoutSpotify: boolean;
  screensaverDelaySeconds: number;
};

type ScreenSettingsResponse = ScreenSettings & {
  hasScreensaverImage: boolean;
};

type BuildScreensaverPreviewPathArgs = {
  publicDeviceId: string;
  cacheBuster: number;
};

type ScreensaverTransform = {
  zoom: number;
  offsetX: number;
  offsetY: number;
};

type ScreensaverDraft = {
  fileName: string;
  image: HTMLImageElement;
};

type ScreensaverRenderLayout = {
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
  clampedOffsetX: number;
  clampedOffsetY: number;
};

type DrawScreensaverPreviewArgs = {
  canvas: HTMLCanvasElement;
  image: HTMLImageElement;
  transform: ScreensaverTransform;
};

type ClampScreensaverTransformArgs = {
  image: HTMLImageElement;
  transform: ScreensaverTransform;
};

const DEFAULT_SCREENSAVER_TRANSFORM: ScreensaverTransform = {
  zoom: 1,
  offsetX: 0,
  offsetY: 0,
};

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), maximum);

const loadImageFile = async (file: File) => {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Failed to load selected image"));
      element.src = objectUrl;
    });

    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      throw new Error("Selected image has invalid dimensions");
    }

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const getScreensaverRenderLayout = ({ image, transform }: ClampScreensaverTransformArgs): ScreensaverRenderLayout => {
  const baseScale = Math.max(
    SCREEN_FRAME_WIDTH / image.naturalWidth,
    SCREEN_FRAME_HEIGHT / image.naturalHeight,
  );
  const drawWidth = image.naturalWidth * baseScale * transform.zoom;
  const drawHeight = image.naturalHeight * baseScale * transform.zoom;
  const maximumOffsetX = Math.max(0, (drawWidth - SCREEN_FRAME_WIDTH) / 2);
  const maximumOffsetY = Math.max(0, (drawHeight - SCREEN_FRAME_HEIGHT) / 2);
  const clampedOffsetX = clamp(transform.offsetX, -maximumOffsetX, maximumOffsetX);
  const clampedOffsetY = clamp(transform.offsetY, -maximumOffsetY, maximumOffsetY);

  return {
    drawWidth,
    drawHeight,
    drawX: (SCREEN_FRAME_WIDTH - drawWidth) / 2 + clampedOffsetX,
    drawY: (SCREEN_FRAME_HEIGHT - drawHeight) / 2 + clampedOffsetY,
    clampedOffsetX,
    clampedOffsetY,
  };
};

const drawScreensaverPreview = ({ canvas, image, transform }: DrawScreensaverPreviewArgs) => {
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create canvas context for screensaver preview");
  }

  const layout = getScreensaverRenderLayout({ image, transform });

  context.clearRect(0, 0, SCREEN_FRAME_WIDTH, SCREEN_FRAME_HEIGHT);
  context.fillStyle = "#111111";
  context.fillRect(0, 0, SCREEN_FRAME_WIDTH, SCREEN_FRAME_HEIGHT);
  context.imageSmoothingEnabled = true;
  context.drawImage(image, layout.drawX, layout.drawY, layout.drawWidth, layout.drawHeight);

  return layout;
};

const rgb565FrameFromCanvas = (canvas: HTMLCanvasElement) => {
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create canvas context for screensaver upload");
  }

  const imageData = context.getImageData(0, 0, SCREEN_FRAME_WIDTH, SCREEN_FRAME_HEIGHT);
  const frameBytes = new Uint8Array(RGB565_FRAME_BYTES);

  for (let index = 0; index < SCREEN_FRAME_WIDTH * SCREEN_FRAME_HEIGHT; index += 1) {
    const pixelOffset = index * 4;
    const red = imageData.data[pixelOffset] ?? 0;
    const green = imageData.data[pixelOffset + 1] ?? 0;
    const blue = imageData.data[pixelOffset + 2] ?? 0;
    const value = ((red & 0xf8) << 8) | ((green & 0xfc) << 3) | (blue >> 3);

    frameBytes[index * 2] = value & 0xff;
    frameBytes[index * 2 + 1] = value >> 8;
  }

  return frameBytes;
};

/*
 * Builds the configuration link that guides users to the main dashboard with the device pre-selected.
 * Inputs: args.publicDeviceId - the identifier assigned to the physical panel.
 * Outputs: A fully-qualified URL string pointing at the dashboard configure route.
 */
const buildConfigurationLink = ({ publicDeviceId }: BuildConfigurationLinkArgs) => {
  const url = new URL(window.location.origin);
  url.pathname = "/";
  url.searchParams.set("device", publicDeviceId);
  return url.toString();
};

const buildScreensaverPreviewPath = ({ publicDeviceId, cacheBuster }: BuildScreensaverPreviewPathArgs) => {
  const params = new URLSearchParams({ v: cacheBuster.toString() });
  return `/api/configure/screen/${publicDeviceId}/screensaver/preview?${params.toString()}`;
};

const STANDALONE_CONFIG_DEVICE_ID_STORAGE_KEY = "tilee-standalone-config-device-id";

const getScreensaverDelayOptionIndex = (seconds: number) => {
  const matchedIndex = SCREEN_SCREENSAVER_DELAY_OPTIONS.findIndex((option) => option === seconds);

  if (matchedIndex !== -1) {
    return matchedIndex;
  }

  let closestIndex = 0;

  for (const [index, option] of SCREEN_SCREENSAVER_DELAY_OPTIONS.entries()) {
    const currentDistance = Math.abs(option - seconds);
    const closestDistance = Math.abs(SCREEN_SCREENSAVER_DELAY_OPTIONS[closestIndex]! - seconds);

    if (currentDistance < closestDistance) {
      closestIndex = index;
    }
  }

  return closestIndex;
};

/*
 * Presents guidance for pairing and configuring a Tilee panel, summarising next steps after scanning the QR code.
 * Inputs: props.publicDeviceId - the identifier to display within helper content and deep links.
 * Outputs: React component tree containing instructions, device metadata and a quick action button.
 */
export const ConfigureScreenApp = (props: ConfigureScreenAppProps) => {
  const screensaverEditorCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const screensaverFileInputRef = useRef<HTMLInputElement | null>(null);
  const screensaverDragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);
  const [status, setStatus] = useState<"loading" | "linked" | "needs-link" | "error">("loading");
  const [isLinking, setIsLinking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [spotifyProfile, setSpotifyProfile] = useState<SpotifyProfile | undefined>(undefined);

  const configurationLink = useMemo(() => {
    try {
      return buildConfigurationLink({ publicDeviceId: props.publicDeviceId });
    } catch (error) {
      console.error("Failed to build configuration link", {
        code: "CONFIGURE_LINK_BUILD_FAILED",
        error,
        publicDeviceId: props.publicDeviceId,
      });
      return undefined;
    }
  }, [props.publicDeviceId]);

  const fetchStatus = ({ publicDeviceId }: FetchStatusArgs) => {
    setStatus("loading");
    setErrorMessage(undefined);
    setSpotifyProfile(undefined);

    void ResultAsync.fromPromise(
      fetch(`/api/configure/status/${publicDeviceId}`),
      (error) => ({ code: "CONFIGURE_STATUS_REQUEST_FAILED", error })
    )
      .andThen((response) => {
        if (!response.ok) {
          return errAsync({
            code: "CONFIGURE_STATUS_HTTP_ERROR",
            error: new Error(`Status request failed with ${response.status}`),
          });
        }

        return ResultAsync.fromPromise(
          response.json() as Promise<ConfigureStatusResponse>,
          (error) => ({ code: "CONFIGURE_STATUS_JSON_ERROR", error })
        );
      })
      .map((payload) => {
        setStatus(payload.linked ? "linked" : "needs-link");
        setErrorMessage(undefined);
        setSpotifyProfile(payload.profile);
      })
      .mapErr((fetchError) => {
        console.error("Failed to retrieve Spotify configuration status", fetchError);
        setStatus("error");
        setErrorMessage("Unable to verify Spotify status. Please try again.");
      });
  };

  useEffect(() => {
    fetchStatus({ publicDeviceId: props.publicDeviceId });
  }, [props.publicDeviceId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STANDALONE_CONFIG_DEVICE_ID_STORAGE_KEY, props.publicDeviceId);
    } catch (error) {
      console.error("Failed to persist standalone configure screen", {
        code: "STANDALONE_CONFIG_PERSIST_FAILED",
        error,
        publicDeviceId: props.publicDeviceId,
      });
    }
  }, [props.publicDeviceId]);

  useEffect(() => {
    if (status !== "linked") {
      return;
    }

    const storage = (() => {
      try {
        return window.localStorage;
      } catch (error) {
        console.error("Failed to access local storage for confetti flag", {
          code: "CONFIGURE_CONFETTI_STORAGE_UNAVAILABLE",
          error,
          publicDeviceId: props.publicDeviceId,
        });
        return undefined;
      }
    })();

    if (!storage) {
      return;
    }

    const confettiKey = `${props.publicDeviceId}-${status}-confetti-shown`;

    if (storage.getItem(confettiKey)) {
      return;
    }

    confetti({
      particleCount: 220,
      spread: 120,
      origin: { y: 0.6 },
      scalar: 1.1,
    });

    storage.setItem(confettiKey, "true");
  }, [props.publicDeviceId, status]);

  const handleOpenConfiguration = () => {
    if (!configurationLink) {
      return;
    }

    window.open(configurationLink, "_blank", "noopener,noreferrer");
  };

  const handleAuthoriseSpotify = () => {
    setIsLinking(true);
    setErrorMessage(undefined);

    void ResultAsync.fromPromise(
      fetch(`/api/configure/spotify/authorise`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ publicDeviceId: props.publicDeviceId } satisfies AuthoriseSpotifyArgs),
      }),
      (error) => ({ code: "CONFIGURE_AUTHORISE_REQUEST_FAILED", error })
    )
      .andThen((response) => {
        if (!response.ok) {
          return errAsync({
            code: "CONFIGURE_AUTHORISE_HTTP_ERROR",
            error: new Error(`Authorise request failed with ${response.status}`),
          });
        }

        return ResultAsync.fromPromise(
          response.json() as Promise<AuthoriseSpotifyResponse>,
          (error) => ({ code: "CONFIGURE_AUTHORISE_JSON_ERROR", error })
        );
      })
      .map((payload) => {
        window.location.href = payload.authoriseUrl;
      })
      .mapErr((authoriseError) => {
        console.error("Failed to start Spotify authorisation", authoriseError);
        setIsLinking(false);
        setErrorMessage("Unable to start Spotify authorisation. Please try again.");
      });
  };

  const handleUnlinkSpotify = () => {
    setIsLinking(true);
    setErrorMessage(undefined);

    void ResultAsync.fromPromise(
      fetch(`/api/configure/spotify/unlink`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ publicDeviceId: props.publicDeviceId }),
      }),
      (error) => ({ code: "CONFIGURE_UNLINK_REQUEST_FAILED", error })
    )
      .andThen((response) => {
        if (!response.ok) {
          return errAsync({
            code: "CONFIGURE_UNLINK_HTTP_ERROR",
            error: new Error(`Unlink request failed with ${response.status}`),
          });
        }

        return ResultAsync.fromPromise(response.json() as Promise<{ unlinked: boolean }>, (error) => ({
          code: "CONFIGURE_UNLINK_JSON_ERROR",
          error,
        }));
      })
      .map(() => {
        setIsLinking(false);
        fetchStatus({ publicDeviceId: props.publicDeviceId });
      })
      .mapErr((unlinkError) => {
        console.error("Failed to unlink Spotify", unlinkError);
        setIsLinking(false);
        setErrorMessage("Unable to unlink Spotify right now. Please try again.");
      });
  };

  const handleCopyDeviceId = () => {
    const copyResult = (() => {
      if (!navigator?.clipboard) {
        console.error("Clipboard API unavailable", {
          code: "CONFIGURE_COPY_UNAVAILABLE",
          publicDeviceId: props.publicDeviceId,
        });
        return undefined;
      }

      return navigator.clipboard
        .writeText(props.publicDeviceId)
        .catch((error) => {
          console.error("Failed to copy device ID", {
            code: "CONFIGURE_COPY_FAILED",
            error,
            publicDeviceId: props.publicDeviceId,
          });
          return undefined;
        });
    })();

    void copyResult;
  };

  const [screenSettings, setScreenSettings] = useState<ScreenSettings | null>(null);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | undefined>(undefined);
  const [originalScreenSettings, setOriginalScreenSettings] = useState<ScreenSettings | null>(null);
  const [hasScreensaverImage, setHasScreensaverImage] = useState(false);
  const [isUploadingScreensaver, setIsUploadingScreensaver] = useState(false);
  const [screensaverFeedback, setScreensaverFeedback] = useState<string | undefined>(undefined);
  const [screensaverPreviewVersion, setScreensaverPreviewVersion] = useState<number | undefined>(undefined);
  const [screensaverPreviewError, setScreensaverPreviewError] = useState(false);
  const [screensaverDraft, setScreensaverDraft] = useState<ScreensaverDraft | null>(null);
  const [screensaverTransform, setScreensaverTransform] = useState<ScreensaverTransform>(DEFAULT_SCREENSAVER_TRANSFORM);
  const [isDraggingScreensaverPreview, setIsDraggingScreensaverPreview] = useState(false);

  const screensaverPreviewPath = useMemo(() => {
    if (!hasScreensaverImage || screensaverPreviewVersion === undefined) {
      return undefined;
    }

    return buildScreensaverPreviewPath({
      publicDeviceId: props.publicDeviceId,
      cacheBuster: screensaverPreviewVersion,
    });
  }, [hasScreensaverImage, props.publicDeviceId, screensaverPreviewVersion]);

  const clearScreensaverDraft = () => {
    setScreensaverDraft(null);
    setScreensaverTransform(DEFAULT_SCREENSAVER_TRANSFORM);
    setIsDraggingScreensaverPreview(false);
    screensaverDragStateRef.current = null;
  };

  const applyScreenSettingsResponse = (response: ScreenSettingsResponse) => {
    const { hasScreensaverImage: responseHasScreensaverImage, ...settings } = response;
    setScreenSettings(settings);
    setOriginalScreenSettings(settings);
    setHasScreensaverImage(responseHasScreensaverImage);
    setScreensaverPreviewVersion(responseHasScreensaverImage ? Date.now() : undefined);
    setScreensaverPreviewError(false);
  };

  useEffect(() => {
    if (!screensaverDraft || !screensaverEditorCanvasRef.current) {
      return;
    }

    const layout = drawScreensaverPreview({
      canvas: screensaverEditorCanvasRef.current,
      image: screensaverDraft.image,
      transform: screensaverTransform,
    });

    if (
      layout.clampedOffsetX !== screensaverTransform.offsetX ||
      layout.clampedOffsetY !== screensaverTransform.offsetY
    ) {
      setScreensaverTransform((previous) => ({
        ...previous,
        offsetX: layout.clampedOffsetX,
        offsetY: layout.clampedOffsetY,
      }));
    }
  }, [screensaverDraft, screensaverTransform]);

  const fetchScreenSettings = () => {
    setIsUpdatingSettings(true);
    setSettingsError(undefined);

    void ResultAsync.fromPromise(
      fetch(`/api/configure/screen/${props.publicDeviceId}/settings`),
      (error) => ({ code: "SCREEN_SETTINGS_REQUEST_FAILED", error })
    )
      .andThen((response) => {
        if (!response.ok) {
          return errAsync({
            code: "SCREEN_SETTINGS_HTTP_ERROR",
            error: new Error(`Settings request failed with ${response.status}`),
          });
        }

        return ResultAsync.fromPromise(
          response.json() as Promise<ScreenSettingsResponse>,
          (error) => ({ code: "SCREEN_SETTINGS_JSON_ERROR", error })
        );
      })
      .map((settingsResponse) => {
        applyScreenSettingsResponse(settingsResponse);
        setIsUpdatingSettings(false);
      })
      .mapErr((fetchError) => {
        console.error("Failed to fetch screen settings", fetchError);
        setSettingsError("Unable to load screen settings. Please try again.");
        setIsUpdatingSettings(false);
      });
  };

  const updateScreenSettings = (newSettings: Partial<ScreenSettings>) => {
    setIsUpdatingSettings(true);
    setSettingsError(undefined);
    setScreensaverFeedback(undefined);

    void ResultAsync.fromPromise(
      fetch(`/api/configure/screen/${props.publicDeviceId}/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSettings),
      }),
      (error) => ({ code: "SCREEN_SETTINGS_UPDATE_FAILED", error })
    )
      .andThen((response) => {
        if (!response.ok) {
          return errAsync({
            code: "SCREEN_SETTINGS_HTTP_ERROR",
            error: new Error(`Settings update failed with ${response.status}`),
          });
        }

        return ResultAsync.fromPromise(
          response.json() as Promise<ScreenSettingsResponse>,
          (error) => ({ code: "SCREEN_SETTINGS_JSON_ERROR", error })
        );
      })
      .map((updatedSettingsResponse) => {
        applyScreenSettingsResponse(updatedSettingsResponse);
        setIsUpdatingSettings(false);
      })
      .mapErr((updateError) => {
        console.error("Failed to update screen settings", updateError);
        setSettingsError("Unable to update screen settings. Please try again.");
        setIsUpdatingSettings(false);
      });
  };

  const handleScreensaverEnabledChange = (enabled: boolean) => {
    if (enabled && !hasScreensaverImage) {
      setSettingsError("Upload a screensaver image before enabling the paused screensaver.");
      return;
    }

    setSettingsError(undefined);
    setScreensaverFeedback(undefined);
    setScreenSettings((previous) => previous ? { ...previous, screensaverEnabled: enabled } : null);
  };

  const handleScreensaverWithoutSpotifyChange = (enabled: boolean) => {
    if (enabled && !hasScreensaverImage) {
      setSettingsError("Upload a screensaver image before enabling photo frame mode.");
      return;
    }

    setSettingsError(undefined);
    setScreensaverFeedback(undefined);
    setScreenSettings((previous) => previous ? { ...previous, showScreensaverWithoutSpotify: enabled } : null);
  };

  const openScreensaverPicker = () => {
    if (isUploadingScreensaver) {
      return;
    }

    screensaverFileInputRef.current?.click();
  };

  const handleScreensaverUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    setIsUploadingScreensaver(true);
    setSettingsError(undefined);
    setScreensaverFeedback(undefined);
    setScreensaverPreviewError(false);

    void (async () => {
      try {
        const image = await loadImageFile(file);
        setScreensaverDraft({ fileName: file.name, image });
        setScreensaverTransform(DEFAULT_SCREENSAVER_TRANSFORM);
      } catch (error) {
        console.error("Failed to prepare screensaver", {
          code: "SCREENSAVER_PREPARE_FAILED",
          error,
          publicDeviceId: props.publicDeviceId,
        });
        setSettingsError("Unable to prepare the screensaver image. Please try a different file.");
      } finally {
        input.value = "";
        setIsUploadingScreensaver(false);
      }
    })();
  };

  const handleScreensaverZoomChange = (value: number) => {
    if (!screensaverDraft) {
      return;
    }

    const nextTransform = {
      ...screensaverTransform,
      zoom: value,
    };
    const layout = getScreensaverRenderLayout({
      image: screensaverDraft.image,
      transform: nextTransform,
    });

    setScreensaverTransform({
      zoom: value,
      offsetX: layout.clampedOffsetX,
      offsetY: layout.clampedOffsetY,
    });
  };

  const handleScreensaverPreviewPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!screensaverDraft) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    screensaverDragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startOffsetX: screensaverTransform.offsetX,
      startOffsetY: screensaverTransform.offsetY,
    };
    setIsDraggingScreensaverPreview(true);
  };

  const handleScreensaverPreviewPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!screensaverDraft || !screensaverDragStateRef.current) {
      return;
    }

    const canvasBounds = event.currentTarget.getBoundingClientRect();
    const pointerDeltaX = (event.clientX - screensaverDragStateRef.current.startClientX) * (SCREEN_FRAME_WIDTH / canvasBounds.width);
    const pointerDeltaY = (event.clientY - screensaverDragStateRef.current.startClientY) * (SCREEN_FRAME_HEIGHT / canvasBounds.height);
    const nextTransform = {
      ...screensaverTransform,
      offsetX: screensaverDragStateRef.current.startOffsetX + pointerDeltaX,
      offsetY: screensaverDragStateRef.current.startOffsetY + pointerDeltaY,
    };
    const layout = getScreensaverRenderLayout({
      image: screensaverDraft.image,
      transform: nextTransform,
    });

    setScreensaverTransform((previous) => ({
      ...previous,
      offsetX: layout.clampedOffsetX,
      offsetY: layout.clampedOffsetY,
    }));
  };

  const handleScreensaverPreviewPointerEnd = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (screensaverDragStateRef.current?.pointerId === event.pointerId) {
      screensaverDragStateRef.current = null;
      setIsDraggingScreensaverPreview(false);
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleUploadScreensaverDraft = () => {
    if (!screensaverEditorCanvasRef.current || !screensaverDraft) {
      return;
    }

    setIsUploadingScreensaver(true);
    setSettingsError(undefined);
    setScreensaverFeedback(undefined);

    void (async () => {
      try {
        const frameBytes = rgb565FrameFromCanvas(screensaverEditorCanvasRef.current as HTMLCanvasElement);
        const response = await fetch(`/api/configure/screen/${props.publicDeviceId}/screensaver`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/octet-stream",
          },
          body: frameBytes,
        });

        if (!response.ok) {
          throw new Error(`Screensaver upload failed with ${response.status}`);
        }

        const payload = await response.json() as ScreenSettingsResponse;
        applyScreenSettingsResponse(payload);
        clearScreensaverDraft();
        setScreensaverFeedback("Screensaver uploaded and enabled for paused playback.");
      } catch (error) {
        console.error("Failed to upload screensaver", {
          code: "SCREENSAVER_UPLOAD_FAILED",
          error,
          publicDeviceId: props.publicDeviceId,
        });
        setSettingsError("Unable to upload the screensaver image. Please try again.");
      } finally {
        setIsUploadingScreensaver(false);
      }
    })();
  };

  const handleDeleteScreensaver = () => {
    if (!hasScreensaverImage) {
      return;
    }

    const confirmed = window.confirm("Remove the saved screensaver from this Tilee?");
    if (!confirmed) {
      return;
    }

    setIsUploadingScreensaver(true);
    setSettingsError(undefined);
    setScreensaverFeedback(undefined);

    void (async () => {
      try {
        const response = await fetch(`/api/configure/screen/${props.publicDeviceId}/screensaver`, {
          method: "DELETE",
        });

        if (!response.ok) {
          throw new Error(`Screensaver delete failed with ${response.status}`);
        }

        const payload = await response.json() as ScreenSettingsResponse;
        applyScreenSettingsResponse(payload);
        clearScreensaverDraft();
        setScreensaverFeedback("Screensaver removed.");
      } catch (error) {
        console.error("Failed to delete screensaver", {
          code: "SCREENSAVER_DELETE_FAILED",
          error,
          publicDeviceId: props.publicDeviceId,
        });
        setSettingsError("Unable to remove the screensaver right now. Please try again.");
      } finally {
        setIsUploadingScreensaver(false);
      }
    })();
  };

  const handleRetry = () => {
    fetchStatus({ publicDeviceId: props.publicDeviceId });
  };

  useEffect(() => {
    fetchScreenSettings();
  }, [props.publicDeviceId]);

  const screensaverDelayOptionIndex = useMemo(() => {
    if (!screenSettings) {
      return 0;
    }

    return getScreensaverDelayOptionIndex(screenSettings.screensaverDelaySeconds);
  }, [screenSettings]);

  const hasUnsavedChanges = useMemo(() => {
    if (!screenSettings || !originalScreenSettings) {
      return false;
    }
    return screenSettings.brightness !== originalScreenSettings.brightness ||
           screenSettings.screenRotation !== originalScreenSettings.screenRotation ||
           screenSettings.screensaverEnabled !== originalScreenSettings.screensaverEnabled ||
           screenSettings.showScreensaverWithoutSpotify !== originalScreenSettings.showScreensaverWithoutSpotify ||
           screenSettings.screensaverDelaySeconds !== originalScreenSettings.screensaverDelaySeconds;
  }, [screenSettings, originalScreenSettings]);

  return (
    <main className="min-h-screen bg-background px-3 py-4 text-foreground sm:px-4 sm:py-8">
      <div className={cn("mx-auto w-full", "max-w-3xl")}>
        <Card className="shadow-lg">
          <CardHeader className="space-y-2 px-4 pb-4 sm:px-6">
            <CardTitle className="text-2xl sm:text-3xl">Screen configuration</CardTitle>
            <CardDescription>Spotify link, display settings, and paused screensaver.</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            <section className="space-y-5">
              {status === "loading" && (
                <p className="text-sm text-muted-foreground">Checking Spotify connection…</p>
              )}

              {screenSettings && (
                <label className="flex items-start gap-3 rounded-lg border border-border/60 bg-card px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-border"
                    checked={screenSettings.showScreensaverWithoutSpotify}
                    onChange={(event) => handleScreensaverWithoutSpotifyChange(event.target.checked)}
                    disabled={isUpdatingSettings || isUploadingScreensaver || !hasScreensaverImage}
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">Use as photo frame</p>
                    <p className="text-sm text-muted-foreground">
                      {hasScreensaverImage
                        ? "Always show the saved image instead of Spotify art or the linking QR."
                        : "Upload a screensaver to enable this."}
                    </p>
                  </div>
                </label>
              )}

              {status === "linked" && (
                <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    {spotifyProfile?.avatarUrl ? (
                      <img
                        src={spotifyProfile.avatarUrl}
                        alt={spotifyProfile.displayName ?? "Spotify profile"}
                        className="size-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="size-12 rounded-full bg-muted" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Spotify connected</p>
                      <p className="truncate text-sm text-muted-foreground">{spotifyProfile?.displayName ?? "Spotify account"}</p>
                    </div>
                  </div>

                  <Button type="button" variant="outline" onClick={handleUnlinkSpotify} disabled={isLinking} className="w-full sm:w-auto">
                    {isLinking ? "Removing Spotify access…" : "Unlink"}
                  </Button>
                </div>
              )}

              {status === "needs-link" && (
                <div className="rounded-lg border border-border/60 bg-card p-4">
                  <Button type="button" onClick={handleAuthoriseSpotify} disabled={isLinking} className="w-full sm:w-auto">
                    {isLinking ? "Starting Spotify authorisation…" : "Link Spotify to Tilee"}
                  </Button>
                </div>
              )}

              {status === "error" && (
                <div className="space-y-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 size-5 text-destructive" />
                    <div>
                      <p className="font-medium text-destructive">We could not verify the Spotify connection</p>
                      <p className="text-muted-foreground">{errorMessage}</p>
                    </div>
                  </div>
                  <Button type="button" variant="outline" onClick={handleRetry} className="w-full sm:w-auto">
                    Retry status check
                  </Button>
                </div>
              )}

              {screenSettings && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2">
                    <Settings className="size-5 text-primary" />
                    <h3 className="text-lg font-semibold">Display</h3>
                    {hasUnsavedChanges && (
                      <div className="ml-auto flex h-6 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                        <AlertCircle className="h-3 w-3" />
                        Unsaved
                      </div>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-3 rounded-lg border border-border/60 bg-card p-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="brightness">Brightness</Label>
                        <span className="text-sm text-muted-foreground">{screenSettings.brightness}</span>
                      </div>
                      <Slider
                        id="brightness"
                        min={0}
                        max={255}
                        step={1}
                        value={[screenSettings.brightness]}
                        onValueChange={(value) => {
                          const newBrightness = value[0];
                          if (newBrightness !== undefined) {
                            setScreenSettings(prev => prev ? { ...prev, brightness: newBrightness } : null);
                          }
                        }}
                        disabled={isUpdatingSettings}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2 rounded-lg border border-border/60 bg-card p-4">
                      <Label htmlFor="screenRotation">Screen rotation</Label>
                      <Select
                        value={screenSettings.screenRotation.toString()}
                        onValueChange={(value) => {
                          const newRotation = parseInt(value);
                          setScreenSettings(prev => prev ? { ...prev, screenRotation: newRotation } : null);
                        }}
                        disabled={isUpdatingSettings}
                      >
                        <SelectTrigger className="w-full bg-background text-foreground">
                          <SelectValue placeholder="Select rotation" />
                        </SelectTrigger>
                        <SelectContent className="border border-border/60 bg-background text-foreground">
                          <SelectItem value="0">0° (Normal)</SelectItem>
                          <SelectItem value="90">90° (Clockwise)</SelectItem>
                          <SelectItem value="180">180° (Upside down)</SelectItem>
                          <SelectItem value="-90">-90° (Counter-clockwise)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-4 rounded-xl border border-border/60 bg-card p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Label htmlFor="screensaverUpload" className="text-base font-semibold">Paused screensaver</Label>
                        <p className="text-sm text-muted-foreground">Tap the image to choose or replace it.</p>
                      </div>
                    </div>

                    <input
                      ref={screensaverFileInputRef}
                      id="screensaverUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleScreensaverUpload}
                      disabled={isUploadingScreensaver}
                      className="hidden"
                    />

                    {hasScreensaverImage && !screensaverDraft && (
                      <div className="flex justify-start">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleDeleteScreensaver}
                          disabled={isUploadingScreensaver}
                          className="w-full sm:w-auto"
                        >
                          Remove screensaver
                        </Button>
                      </div>
                    )}

                    {!screensaverDraft && !hasScreensaverImage && (
                      <button
                        type="button"
                        onClick={openScreensaverPicker}
                        disabled={isUploadingScreensaver}
                        className="grid w-full gap-3 rounded-lg border border-dashed border-border/60 bg-background/60 p-2 text-left transition hover:border-foreground/30 disabled:cursor-not-allowed disabled:opacity-60 sm:p-4 md:grid-cols-[minmax(0,192px),1fr] md:items-center"
                      >
                        <div className="mx-auto flex aspect-square w-full max-w-72 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-[#111111] p-1.5 shadow-inner sm:max-w-56 md:max-w-48 sm:p-2">
                          <div className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed border-white/20 text-center text-xs text-muted-foreground [image-rendering:pixelated]">
                            Choose image
                          </div>
                        </div>

                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">No screensaver uploaded</p>
                          <p className="text-sm text-muted-foreground">Choose an image to create the 64x64 crop.</p>
                        </div>
                      </button>
                    )}

                    {screensaverDraft && (
                      <div className="grid gap-4 rounded-lg border border-border/60 bg-background/60 p-2 sm:p-4 md:grid-cols-[minmax(0,192px),1fr] md:items-center">
                        <div className="mx-auto flex aspect-square w-full max-w-72 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-[#111111] p-1.5 shadow-inner sm:max-w-56 md:max-w-48 sm:p-2">
                          <div className="w-full">
                            <canvas
                              ref={screensaverEditorCanvasRef}
                              width={SCREEN_FRAME_WIDTH}
                              height={SCREEN_FRAME_HEIGHT}
                              className={cn(
                                "aspect-square w-full rounded-md touch-none select-none [image-rendering:pixelated]",
                                isDraggingScreensaverPreview ? "cursor-grabbing" : "cursor-grab",
                              )}
                              onPointerDown={handleScreensaverPreviewPointerDown}
                              onPointerMove={handleScreensaverPreviewPointerMove}
                              onPointerUp={handleScreensaverPreviewPointerEnd}
                              onPointerCancel={handleScreensaverPreviewPointerEnd}
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground">Crop before upload</p>
                            <p className="text-sm text-muted-foreground">Drag to move. Zoom to fill the square. No stretching.</p>
                            <p className="truncate text-xs text-muted-foreground">{screensaverDraft.fileName}</p>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="screensaverZoom">Zoom</Label>
                              <span className="text-sm text-muted-foreground">{screensaverTransform.zoom.toFixed(2)}x</span>
                            </div>
                            <Slider
                              id="screensaverZoom"
                              min={1}
                              max={4}
                              step={0.01}
                              value={[screensaverTransform.zoom]}
                              onValueChange={(value) => {
                                const nextZoom = value[0];
                                if (nextZoom !== undefined) {
                                  handleScreensaverZoomChange(nextZoom);
                                }
                              }}
                              disabled={isUploadingScreensaver}
                              className="w-full"
                            />
                          </div>

                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                              type="button"
                              onClick={handleUploadScreensaverDraft}
                              disabled={isUploadingScreensaver}
                              className="w-full sm:w-auto"
                            >
                              {isUploadingScreensaver ? "Uploading…" : "Upload crop"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={clearScreensaverDraft}
                              disabled={isUploadingScreensaver}
                              className="w-full sm:w-auto"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {!screensaverDraft && hasScreensaverImage && (
                      <div className="grid gap-4 rounded-lg border border-border/60 bg-background/60 p-2 sm:p-4 md:grid-cols-[minmax(0,192px),1fr] md:items-center">
                        <button
                          type="button"
                          onClick={openScreensaverPicker}
                          disabled={isUploadingScreensaver}
                          className="mx-auto flex aspect-square w-full max-w-72 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-[#111111] p-1.5 text-left shadow-inner transition hover:border-foreground/30 disabled:cursor-not-allowed disabled:opacity-60 sm:max-w-56 md:max-w-48 sm:p-2"
                        >
                          <div className="w-full">
                            {screensaverPreviewPath && !screensaverPreviewError ? (
                              <img
                                src={screensaverPreviewPath}
                                alt="Uploaded screensaver preview"
                                className="aspect-square w-full rounded-md object-cover [image-rendering:pixelated]"
                                style={{ transform: `rotate(${screenSettings.screenRotation}deg)` }}
                                onLoad={() => setScreensaverPreviewError(false)}
                                onError={() => setScreensaverPreviewError(true)}
                              />
                            ) : (
                              <div className="px-3 text-center text-xs text-muted-foreground">
                                Preview unavailable right now.
                              </div>
                            )}
                          </div>
                        </button>

                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">Current upload</p>
                          <p className="text-sm text-muted-foreground">Tap the image to replace it.</p>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3 rounded-lg border border-border/60 bg-background/60 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="screensaverDelay">Show after pause</Label>
                        <span className="text-sm text-muted-foreground">
                          {formatScreensaverDelayLabel(screenSettings.screensaverDelaySeconds)}
                        </span>
                      </div>
                      <Slider
                        id="screensaverDelay"
                        min={0}
                        max={SCREEN_SCREENSAVER_DELAY_OPTIONS.length - 1}
                        step={1}
                        value={[screensaverDelayOptionIndex]}
                        onValueChange={(value) => {
                          const nextIndex = value[0];
                          if (nextIndex === undefined) {
                            return;
                          }

                          const nextDelay = SCREEN_SCREENSAVER_DELAY_OPTIONS[nextIndex];
                          if (nextDelay === undefined) {
                            return;
                          }

                          setScreenSettings((previous) => previous ? {
                            ...previous,
                            screensaverDelaySeconds: nextDelay,
                          } : null);
                        }}
                        disabled={isUpdatingSettings}
                        className="w-full"
                      />
                    </div>

                    <label
                      className={cn(
                        "flex items-start gap-3 rounded-lg border border-border/60 bg-background/60 px-3 py-3 transition-opacity",
                        hasScreensaverImage
                          ? "cursor-pointer"
                          : "pointer-events-none cursor-default opacity-40",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-border"
                        checked={hasScreensaverImage && screenSettings.screensaverEnabled}
                        onChange={(event) => handleScreensaverEnabledChange(event.target.checked)}
                        disabled={isUpdatingSettings || isUploadingScreensaver || !hasScreensaverImage}
                      />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">Use screensaver while music is paused</p>
                      </div>
                    </label>

                    {screensaverFeedback && (
                      <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm">
                        <CheckCircle2 className="mt-0.5 size-5 text-emerald-600" />
                        <p className="text-emerald-900">{screensaverFeedback}</p>
                      </div>
                    )}
                  </div>

                  {settingsError && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 size-5 text-destructive" />
                        <div>
                          <p className="font-medium text-destructive">Settings error</p>
                          <p className="text-muted-foreground">{settingsError}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-stretch sm:justify-end">
                    <Button 
                      type="button" 
                      onClick={() => screenSettings && updateScreenSettings(screenSettings)}
                      disabled={isUpdatingSettings || !screenSettings || !hasUnsavedChanges}
                      className="w-full sm:w-auto"
                    >
                      {isUpdatingSettings 
                        ? "Saving…" 
                        : hasUnsavedChanges 
                          ? "Save changes" 
                          : "Saved"
                      }
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};
